import { Box, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Fade, IconButton } from 'utoniq-ui'

import {
  ItemThumbnailType,
  PortfolioConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { ItemReversibleThumbnail } from '@/components/organisms'
import { VideoPlayer } from '@/components/organisms/resourcePlayer/VideoPlayer'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Privacy } from '@/core/native'

import type { State } from '../../../viewModel'
import { ItemDescriptionCell } from '../ItemDescription'
import { SupplementLinkSetCell } from '../SupplementLinkSet'

export type Props = {
  loading: boolean
  portfolio: State['portfolio']
  isFlipped: boolean
  onReverseThumbnail: () => void
  onClickProtectUserItem: () => void
  onOpenFullScreen?: () => void
  onOpenMultipleImage?: () => void
}
export const OwnedItemDetailVideoView: Organism<Props> = ({
  loading,
  portfolio,
  isFlipped,
  onClickProtectUserItem,
  onReverseThumbnail,
  onOpenFullScreen,
  onOpenMultipleImage,
}) => {
  const [displayResource, setDisplayResource] = useState(false)
  const videoResource = portfolio?.item?.resource
  const onClickItemButton = () => setDisplayResource(prev => !prev)

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <Stack
          alignItems="center"
          sx={{
            position: 'relative',
            '.MuiButtonBase-root': {
              height: 'fit-content',
            },
          }}
        >
          <ThumbnailActionAreaCell
            onOpenFullScreen={onOpenFullScreen}
            onOpenMultipleImage={onOpenMultipleImage}
            numberImage={portfolio?.item.images?.length ?? 0}
          />
          <ItemReversibleThumbnail
            loading={loading}
            flipProps={{
              isFlipped,
            }}
            btnPlay={{
              type: 'back',
              onClick: onClickItemButton,
            }}
            imageAreaProps={{
              thumbnail: portfolio?.item?.thumbnail,
              size:
                portfolio?.item.thumbnailType === ItemThumbnailType.Square
                  ? 'x-large-square'
                  : 'x-large',
              onClick: onReverseThumbnail,
            }}
          />
        </Stack>
        <ItemDescriptionCell portfolio={portfolio} />
        <Box sx={{ mt: 4, mb: 2 }}>
          {videoResource && !isFlipped ? (
            <IconButton.Video
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          ) : (
            <IconButton.Rotate
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          )}
        </Box>
      </Stack>
      <SupplementLinkSetCell
        sku={portfolio?.item.sku}
        itemId={portfolio?.item.id}
        commentCount={portfolio?.item.commentCount}
        relatedMissions={portfolio?.item.relatedMissions}
        artistId={portfolio?.item.issuedArtist?.id ?? ''}
        isUserProtected={portfolio?.isUserProtected}
        issuableNft={!!portfolio?.item.nft?.chains}
        onClickProtectUserItem={onClickProtectUserItem}
      />
      <ItemResourcePlayerCell
        open={displayResource}
        onClose={onClickItemButton}
        item={portfolio?.item}
      />
    </CellFrame>
  )
}

export const ItemResourcePlayerCell: Cell<{
  item?: PortfolioConnectionFragment['item']
  open: boolean
  onClose: () => void
}> = ({ item, open, onClose }) => {
  return (
    <CellFrame>
      {item?.resource && (
        <Fade>
          <VideoPlayer
            open={open}
            autoplay={open}
            onClose={onClose}
            resource={item?.resource}
          />
        </Fade>
      )}
    </CellFrame>
  )
}

export function usePrivacyScreen() {
  useEffect(() => {
    Privacy.enablePrivacyScreen()

    return () => {
      Privacy.disablePrivacyScreen()
    }
  })
}
